import { Flex, Label, Text } from '@approvalmax/ui/src/components';
import { domain } from 'modules/data';
import { XeroAddressType } from 'pages/requestForm/data/xero/XeroAddress';
import { FC, memo, useMemo } from 'react';
import { useCompanyBasedOnUrl } from 'shared/hooks/useCompanyBasedOnUrl';

import { messages } from './XeroContactDetails.messages';
import { Root, StyledGrid } from './XeroContactDetails.styles';
import { XeroContactDetailsProps } from './XeroContactDetails.types';

export const XeroContactDetails: FC<XeroContactDetailsProps> = memo((props) => {
    const { contact } = props;

    const company = useCompanyBasedOnUrl();
    const isHideBatchPaymentInContactBeta = company.betaFeatures.includes(
        domain.CompanyBetaFeature.HideBatchPaymentInContact
    );

    const billingAddress = useMemo(() => {
        let selectedAddress = contact?.addresses?.find((address) => address.type === XeroAddressType.Billing);

        if (!selectedAddress) {
            selectedAddress = contact?.addresses?.find((address) => address.type === XeroAddressType.Delivery);
        }

        if (selectedAddress?.address) {
            const { address } = selectedAddress;
            const addressParts = [
                [address.addressLine1, address.addressLine2, address.addressLine3, address.addressLine4]
                    .filter(Boolean)
                    .join(', '),
                [address.city, address.region, address.postalCode, address.country].filter(Boolean).join(', '),
            ];

            return addressParts.filter(Boolean);
        }

        return [];
    }, [contact?.addresses]);

    if (!contact) return null;

    const { accountNumber, taxNumber, emailAddress } = contact;

    return (
        <Root spacing='4 16' alignItems='start' wrap={false}>
            {billingAddress.length > 0 && (
                <Flex spacing='4' grow={1} minWidth={150}>
                    <Label spacing='0'>{messages.address}</Label>

                    <Flex spacing='4 0' direction='column'>
                        {billingAddress.map((addressLine, index) => (
                            <Text key={index} font='label' fontSize='small'>
                                {addressLine}
                            </Text>
                        ))}
                    </Flex>
                </Flex>
            )}

            {((accountNumber && !isHideBatchPaymentInContactBeta) || taxNumber || emailAddress) && (
                <StyledGrid gridTemplateColumns='auto 1fr' columnGap={4}>
                    {accountNumber && !isHideBatchPaymentInContactBeta && (
                        <>
                            <Label>{messages.accountNumber}</Label>

                            <Text font='label' fontSize='small'>
                                {accountNumber}
                            </Text>
                        </>
                    )}

                    {taxNumber && (
                        <>
                            <Label>{messages.taxId}</Label>

                            <Text font='label' fontSize='small'>
                                {taxNumber}
                            </Text>
                        </>
                    )}

                    {emailAddress && (
                        <>
                            <Label>{messages.email}</Label>

                            <Text font='label' fontSize='small'>
                                {emailAddress}
                            </Text>
                        </>
                    )}
                </StyledGrid>
            )}
        </Root>
    );
});

XeroContactDetails.displayName = 'XeroContactDetails';
